'use client';

import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { ServerLink } from '../../../helpers';
import { AccountInfo } from '../../UserBlock';
import { SignOutButton } from './SignOutButton';
import { AnalyticsEventName } from '@/api/analytics';
import {
  AnimatedItem,
  AnimatedWrapper,
} from '@/components/animation/AnimatedItem';
import { Menu, MenuProps } from '@/components/general/Menu';
import { ModalSelectContainer } from '@/components/ModalSelect';
import { NamesSteps } from '@/helpers/auth';
import { generateLink } from '@/helpers/common';
import { resetCachePayments } from '@/helpers/payments';
import { useAnalytics } from '@/hooks/analytics';
import useEstonia from '@/hooks/estonia';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';

interface ModalNavProps extends MenuProps {
  locale: string;
  isHidden?: boolean;
  tabBarTitle: string;
  onClose: () => void;
  links: ServerLink[];
}
export const BOTTOM_GAP = 2;

export const ModalNav = ({
  locale,
  isHidden,
  tabBarTitle,
  onClose,
  links,
}: ModalNavProps) => {
  const { push } = useRouter();

  const { sendEventToAnalytics } = useAnalytics();

  const navigate = useCallback(
    (link: string, scrollToTop: boolean) => {
      push(generateLink(link, locale), { scroll: scrollToTop });
    },
    [locale, push]
  );

  const { menuIcon: MenuIcon } = useTheme();

  const { isEstonia } = useEstonia();

  const { isAuthenticated, resetUser, tabBarHeight } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      resetUser: state.resetUser,
      tabBarHeight: state.tabBarHeight,
    }))
  );

  const handleSignOut = useCallback(() => {
    useLocalStorageStore.getState().remove(STORAGE.HAS_SHOWN_LIMIT_MODAL);
    sendEventToAnalytics({
      name: AnalyticsEventName.TABBAR__SIGN_OUT__BUTTON__CLICK,
    });
    useCommonStore.setState({
      currentRegistrationStep: NamesSteps.PRIMARY_INFORMATION,
      isAdditionalUserDataForm: false,
    });
    onClose();
    resetUser();
    resetCachePayments();
    navigate('/', true);

    Sentry.addBreadcrumb({
      category: 'Sign out',
      level: 'info',
    });
  }, [onClose, resetUser, navigate, sendEventToAnalytics]);

  return (
    <ModalSelectContainer
      icon={<MenuIcon />}
      title={tabBarTitle}
      onClose={onClose}
      bottom={tabBarHeight - BOTTOM_GAP}
      padding="16px 0 0 0"
      innerAlignBottom={!isAuthenticated}
      isHidden={isHidden}
      isEstonia={isEstonia}
    >
      <AnimatedWrapper>
        <AnimatedItem>
          {isAuthenticated && <AccountInfo onClose={onClose} />}
          <Menu
            locale={locale}
            links={links}
            onClickMenuItem={onClose}
            signOutBtn={
              isAuthenticated ? (
                <SignOutButton onSignOut={handleSignOut} />
              ) : null
            }
          />
        </AnimatedItem>
      </AnimatedWrapper>
    </ModalSelectContainer>
  );
};
